import { InlineAlertType, Query } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { SourceName } from 'features/application/hooks/useApplicationTrackErrorEvents';
import { useStepStartedFocusAnalytics } from 'features/application/hooks/useStepStartedFocusAnalytics';
import useUpdateApplication from 'features/application/hooks/useUpdateApplication';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import GetEmployment from 'services/graphql/queries/Employment';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isWeb } from 'shared/constants/platform';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';

type EmploymentRoute =
  '/reactivation/review-info' | '/signup/citizenship' | '/signup/dob';

interface Props {
  applicationId: string | undefined;
  nextRoute: EmploymentRoute;
  sourceName: SourceName;
  error?: Error | null;
  onRetry: () => void;
  backRoute: EmploymentRoute;
  title: string;
  description: string;
  analyticsStepName: string;
  isReactivation?: boolean;
  applicationDataLoading?: boolean;
  hasApplicationLoaded: boolean;
  isReapplication: boolean;
}

const Employment = ({
  applicationId,
  nextRoute,
  backRoute,
  sourceName,
  error,
  onRetry,
  title,
  description,
  analyticsStepName,
  isReactivation = false,
  applicationDataLoading = false,
  hasApplicationLoaded,
  isReapplication,
}: Props) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'employment',
  });
  const insets = useSafeAreaInsets();
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const [{ data, fetching }] = useQuery<Query>({
    query: GetEmployment,
    requestPolicy: 'cache-first',
  });

  const {
    updateApplication,
    updateApplicationResult,
    isUpdateApplicationSuccess,
    isUpdateApplicationLoading,
  } = useUpdateApplication({
    applicationId,
    sourceName,
  });

  const handleBack = useCallback(() => {
    router.replace(backRoute);
  }, [backRoute, router]);

  useBackHandler(handleBack);

  useStepStartedFocusAnalytics(
    hasApplicationLoaded
      ? {
          analyticsId: 'stepStarted',
          customAttributes: {
            step_name: analyticsStepName,
            ...(isReactivation && { reactivation_flow: true }),
            ...(isReapplication && { reapplication_flow: 'true' }),
          },
        }
      : undefined,
  );

  useStepStartedFocusAnalytics(
    isUpdateApplicationSuccess
      ? {
          analyticsId: 'employmentStepCompleted',
          customAttributes: {
            step_name: analyticsStepName,
            ...(isReactivation && { reactivation_flow: true }),
            ...(isReapplication && { reapplication_flow: 'true' }),
          },
        }
      : undefined,
  );

  const handleOccupationSelect = async (occupation: string) => {
    await updateApplication({
      updates: { occupation },
      nextRoute,
    });
  };

  const occupations = data?.occupations;
  const loading =
    fetching || isUpdateApplicationLoading || applicationDataLoading;

  return (
    <>
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={
          <View style={styles.loaderContainer}>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={
          error ? (
            <DefaultPageDownErrorMessage
              error={error}
              title={defaultTitle}
              message={defaultMessage}
              illustrationKey="PLACEHOLDER_A"
              tryAgainAction={onRetry}
              screenAnalyticsEventKey="employmentError"
            />
          ) : null
        }
      />
      <View style={styles.employmentContainer}>
        <Stack.Screen
          options={{
            headerShown: true,
            headerBackVisible: true,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar {...props} primaryOnPress={handleBack} />
            ),
          }}
        />
        {updateApplicationResult.error ? (
          <View style={styles.inlineAlertContainer}>
            <InlineAlert
              alertType={InlineAlertType.Error}
              bodyText={translatedText('inlineAlert.errorTitle')}
              iconKeyType="ERROR"
              onDismiss={() => {}}
            />
          </View>
        ) : null}
        <View style={[styles.container, { paddingBottom: insets.bottom }]}>
          <View style={styles.personalInfoFormContainer}>
            <TitleLgText numberOfLines={0} style={styles.describeStyle}>
              {title}
            </TitleLgText>

            <BodyBaseText numberOfLines={0} style={styles.describeStyle}>
              {description}
            </BodyBaseText>

            {!loading && occupations?.length === 0 ? (
              <BodyBaseText style={styles.emptyMessage}>
                {defaultMessage}
              </BodyBaseText>
            ) : (
              <FlatList
                data={occupations}
                keyExtractor={(item) => item.name}
                ItemSeparatorComponent={() => (
                  <View style={styles.dividerContainer}>
                    <HairLineDivider />
                  </View>
                )}
                renderItem={({ item }) => (
                  <ListItem
                    primaryCopy={item.description}
                    analyticsId="employmentSelected"
                    customAttributes={{
                      employment_selected: item.name?.toLowerCase(),
                    }}
                    onPress={() => handleOccupationSelect(item.name)}
                  />
                )}
                removeClippedSubviews={false}
              />
            )}
          </View>
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    minWidth: '100%',
    paddingVertical: theme.spacing.m,
  },
  describeStyle: {
    paddingBottom: theme.spacing.m,
    paddingLeft: theme.spacing.m,
    paddingRight: theme.spacing.s,
  },
  dividerContainer: {
    marginHorizontal: theme.spacing.m,
  },
  employmentContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexGrow: 1,
    paddingBottom: isWeb ? theme.spacing.l : 0,
  },
  emptyMessage: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.l,
    textAlign: 'center',
  },
  inlineAlertContainer: {
    padding: theme.spacing.m,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  personalInfoFormContainer: {
    display: 'flex',
    flex: 1,
    flexGrow: 1,
    justifyContent: 'flex-start',
    width: '100%',
  },
});

export default Employment;
